/**
 * Smart distractor generation for math facts.
 *
 * Generates plausible wrong answers using neighboring facts,
 * NOT random numbers. This tests actual fluency vs. guessing.
 */

import type { MasteryItem } from './types'

/**
 * Generate 3 distractors for a math fact item.
 * Returns array of wrong answer strings.
 */
export function generateDistractors(item: MasteryItem): string[] {
  const correct = parseInt(item.correctAnswer, 10)
  const operands = item.extraData?.operands ?? []
  const operation = item.extraData?.operation ?? 'multiplication'

  let candidates: number[]

  if (operation === 'multiplication') {
    candidates = multiplicationDistractors(operands[0], operands[1], correct)
  } else if (operation === 'division') {
    candidates = divisionDistractors(operands[0], operands[1], correct)
  } else if (operation === 'addition') {
    candidates = additionDistractors(operands[0], operands[1], correct)
  } else if (operation === 'subtraction') {
    candidates = subtractionDistractors(operands[0], operands[1], correct)
  } else {
    candidates = nearMissDistractors(correct)
  }

  // Filter: no duplicates, no correct answer, no negatives
  const unique = [...new Set(candidates)].filter(
    (n) => n !== correct && n >= 0
  )

  // If we don't have enough, pad with near-misses
  while (unique.length < 3) {
    const offset = unique.length + 1
    const candidate = correct + (offset % 2 === 0 ? offset : -offset)
    if (candidate >= 0 && candidate !== correct && !unique.includes(candidate)) {
      unique.push(candidate)
    } else {
      // Fallback: just add next integer
      const fallback = correct + unique.length + 2
      if (!unique.includes(fallback) && fallback !== correct) {
        unique.push(fallback)
      }
    }
  }

  return unique.slice(0, 3).map(String)
}

/** For a × b = product, use neighboring table facts */
function multiplicationDistractors(a: number, b: number, correct: number): number[] {
  const candidates: number[] = []

  // Same-family neighbor: a × (b±1)
  if (b > 0) candidates.push(a * (b - 1))
  candidates.push(a * (b + 1))

  // Other-factor neighbor: (a±1) × b
  if (a > 0) candidates.push((a - 1) * b)
  candidates.push((a + 1) * b)

  // Near-miss on the product
  candidates.push(correct + 1)
  candidates.push(correct - 1)

  // Swapped digits for two-digit answers (common mistake: 56 → 65)
  if (correct >= 10 && correct < 100) {
    const tens = Math.floor(correct / 10)
    const ones = correct % 10
    if (tens !== ones) {
      candidates.push(ones * 10 + tens)
    }
  }

  return candidates
}

/** For product ÷ divisor = quotient, use neighboring quotients */
function divisionDistractors(dividend: number, divisor: number, correct: number): number[] {
  const candidates: number[] = []

  // Neighboring quotients (what you'd get from nearby dividends)
  candidates.push(correct + 1)
  candidates.push(correct - 1)
  candidates.push(correct + 2)

  // Confusion with the divisor itself (common mistake)
  if (divisor !== correct) {
    candidates.push(divisor)
  }

  // If dividend is a product, confuse with other factor
  // e.g., 56 ÷ 7 = 8, distractor could be 9 (from 63 ÷ 7)
  if (divisor > 0) {
    candidates.push(Math.floor((dividend + divisor) / divisor))
    if (dividend > divisor) {
      candidates.push(Math.floor((dividend - divisor) / divisor))
    }
  }

  return candidates
}

/** For a + b = sum, use near sums plus one decade error.
 *
 * Order matters: only the first three survive. Leading with +1/-1/+2 made every
 * addition contact four consecutive integers, which a kid can solve by looking
 * at the shape of the choices instead of adding. Interleaving a decade error
 * (the classic carrying mistake) forces an actual computation.
 */
function additionDistractors(_a: number, _b: number, correct: number): number[] {
  const candidates: number[] = []

  candidates.push(correct - 1)
  candidates.push(correct + 2)

  // Carrying mistake: 7+8 answered as 5 (dropped the ten) or 25 (doubled it)
  if (correct >= 10) candidates.push(correct - 10)
  candidates.push(correct + 10)

  // Backfill near-misses if the decade errors collapsed into duplicates
  candidates.push(correct + 1)
  candidates.push(correct - 2)

  return candidates
}

/** For total - b = result, use near differences plus the classic wrong answers.
 *
 * Same ordering rule as addition: lead with the mistakes that mean something
 * (answering with the subtrahend, borrowing errors) so the four choices aren't
 * a run of consecutive integers.
 */
function subtractionDistractors(_total: number, b: number, correct: number): number[] {
  const candidates: number[] = []

  candidates.push(b)  // Common mistake: answer with what was subtracted
  candidates.push(correct - 1)
  candidates.push(correct + 2)

  // Borrowing error
  if (correct >= 10) candidates.push(correct - 10)
  candidates.push(correct + 10)
  candidates.push(correct + 1)

  return candidates
}

/** Generic near-miss fallback */
function nearMissDistractors(correct: number): number[] {
  return [correct + 1, correct - 1, correct + 2, correct + 3]
}

/**
 * Given an item, produce a Contact with shuffled choices.
 */
export function buildContact(item: MasteryItem) {
  const distractors = generateDistractors(item)
  const allChoices = [item.correctAnswer, ...distractors]

  // Fisher-Yates shuffle
  for (let i = allChoices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allChoices[i], allChoices[j]] = [allChoices[j], allChoices[i]]
  }

  return {
    item,
    distractors,
    allChoices,
    correctIndex: allChoices.indexOf(item.correctAnswer),
  }
}
